import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UxContext } from '../context/UxContext';
import { useGlobalStore } from '../store/globalStore/context/globalContext';
import { usePageStore } from '../store/pageStore/context/pageContext';
import i18n from '../utils/i18n';
import lediumApi from '../utils/lediumApi';

interface UseLediumPageOptions<T extends Record<string, unknown>> {
    postUpdate?: (data: T) => T;
    saveCondition?: () => boolean;
    saveDataMap: (saveFormData?: T | null) => Record<string, unknown>;
    newItemDefaults?: Record<string, unknown>;
}

function useLediumPage<T extends Record<string, unknown>>(
    options: UseLediumPageOptions<T>
): { onSave: (saveFormData?: T | null) => void; mainMenu: () => void } {
    const { postUpdate, saveCondition, saveDataMap, newItemDefaults } = options;
    const { handleSnackbar } = React.useContext(UxContext)!;
    const {
        globalState: { userData }
    } = useGlobalStore();
    const {
        pageState: { formData, pageConfig, apiError },
        pageActions
    } = usePageStore();
    const { pageType, itemId } = useParams<{ pageType?: string; itemId?: string }>();
    const navigate = useNavigate();

    const mainMenu = () => {
        navigate(pageConfig.url as string);
    };

    React.useEffect(() => {
        if (!pageConfig?.fetchEndPoint) {
            return;
        }

        const controller = new AbortController();

        switch (pageType) {
            case 'view':
                pageActions.api.loadItem<T>(itemId as string, postUpdate, { readOnly: true }, controller.signal);
                break;
            case 'edit':
                pageActions.api.loadItem<T>(itemId as string, postUpdate, userData.admin ? {} : { readOnly: true }, controller.signal);
                break;
            case 'new':
                pageActions.formData.overrideFormData({ ...pageConfig.initialData, ...newItemDefaults, loaded: true });
                pageActions.formData.setFormBaseline();
                break;
            default:
                mainMenu();
        }

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageType, itemId, pageConfig]);

    React.useEffect(() => {
        if (!apiError) {
            return;
        }
        handleSnackbar({ message: i18n('load_item_failed') as string, severity: 'error' });
        navigate(pageConfig.url as string);
    }, [apiError, handleSnackbar, navigate, pageConfig.url]);

    function onSave(saveFormData: T | null = null) {
        if (!saveFormData) {
            pageActions.formData.updateFormItem({ isSubmitted: true });
        }

        if (!saveCondition || (saveCondition && saveCondition())) {
            pageActions.formData.setFormSubmitting(true);
            const saveData = saveDataMap(saveFormData);
            const promise = formData.id
                ? lediumApi.patch(`${pageConfig.apiEndPoint}/${formData.id}`, saveData)
                : lediumApi.put(`${pageConfig.apiEndPoint}`, saveData);
            const infoKey = formData.id ? 'X_is_successfully_updated' : 'X_is_successfully_added';

            promise.then((result) => {
                if (!result) {
                    setTimeout(() => pageActions.formData.setFormSubmitting(false), 500);
                    return;
                }

                handleSnackbar({
                    message: i18n(infoKey, pageConfig.itemName) as string
                });

                pageActions.formData.setFormBaseline();
                pageActions.formData.setFormSubmitting(false);
                if (!saveFormData) {
                    mainMenu();
                }
            });
        }
    }

    return { onSave, mainMenu };
}

export default useLediumPage;
